/**
 * AppHeaderController — global topbar/sidebar shell.
 *
 * Bound on every page that uses layouts/app.html. Hydrates the four shell
 * placeholders (topbar org name, topbar avatar initials, sidebar foot owner
 * name, sidebar foot meta line) from `GET /auth/me`.
 *
 * The `accountStatus` value drives the rendering:
 *   - "prospect" : org="Compte gratuit", initials="—",
 *                  footMeta="Compte gratuit · sans SIRET" (or "· SIRET à
 *                  activer" when the user has imported an existing SIRET).
 *   - "active"   : org=organization.legalName, initials from owner name,
 *                  footMeta="SIRET <formatted>".
 *   - ""         : leaves all targets at their HTML default ("—") — safe for
 *                  the auth/blank layouts that bind the controller without
 *                  exposing the targets.
 *
 * If /auth/me fails (other than 401, which api.js handles), the placeholders
 * keep their dashes — graceful degradation.
 *
 * EPIC-042 / 42.4 — gagne un 5ᵉ target `paymentsPendingCount` : badge nav
 * « Transactions à traiter » dans la sidebar account. Hydraté depuis
 * `GET /v1/payments/pending` au `connect()`, décrémenté en live par
 * les controllers `payments-pending` via `CustomEvent('nm:pending-decremented')`
 * dispatché sur `window` (découplage DOM, pas de couplage direct
 * inter-controllers).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

export default class AppHeaderController extends BaseController {
  static targets = ["orgName", "orgInitials", "ownerName", "orgFootMeta", "paymentsPendingCount"];
  static values  = { accountStatus: String };

  async connect() {
    // CustomEvent decrementer — see hydratePaymentsPending().
    this._onPendingDecremented = () => this.decrementPending();
    window.addEventListener("nm:pending-decremented", this._onPendingDecremented);

    try {
      const me = await api.getMeCached();
      this.hydrate(me || {});
    } catch (_error) {
      // 401 → already redirected by api.js. Other errors leave the
      // placeholders ("—") in place.
    }

    // Badge nav « Transactions à traiter » — hydrate en parallèle de /auth/me
    // pour ne pas bloquer la topbar. Échec silencieux : le badge reste hidden.
    this.hydratePaymentsPending();
  }

  disconnect() {
    if (this._onPendingDecremented) {
      window.removeEventListener("nm:pending-decremented", this._onPendingDecremented);
    }
  }

  hydrate(me) {
    const user = me.user || {};
    const org  = me.organization || {};

    const ownerName = this.deriveOwnerName(user);
    const isProspect = this.accountStatusValue === "prospect";

    const orgLabel = isProspect
      ? "Compte gratuit"
      : (org.legalName || org.name || "—");

    const initials = isProspect
      ? "—"
      : this.deriveInitials(user.firstName, user.lastName, user.email);

    const footMeta = isProspect
      ? this.deriveProspectFootMeta(org)
      : (org.siret ? `SIRET ${this.formatSiret(org.siret)}` : "SIRET —");

    if (this.hasOrgNameTarget)     this.orgNameTarget.textContent     = orgLabel;
    if (this.hasOrgInitialsTarget) this.orgInitialsTarget.textContent = initials;
    if (this.hasOwnerNameTarget)   this.ownerNameTarget.textContent   = ownerName;
    if (this.hasOrgFootMetaTarget) this.orgFootMetaTarget.textContent = footMeta;
  }

  deriveOwnerName(user) {
    const parts = [user.firstName, user.lastName].filter(Boolean).join(" ");
    if (parts) return parts;
    return user.email || "—";
  }

  deriveInitials(firstName, lastName, email) {
    const f = (firstName || "").trim();
    const l = (lastName || "").trim();
    if (f || l) {
      return ((f.slice(0, 1) || "") + (l.slice(0, 1) || "")).toUpperCase();
    }
    const e = (email || "").trim();
    return e ? e.slice(0, 2).toUpperCase() : "—";
  }

  deriveProspectFootMeta(org) {
    // Imported existing micro (SIRET present but org.kind === pending activation)
    // is signalled by the backend via siret being set on a free-tier org.
    if (org.siret) return "Compte gratuit · SIRET à activer";
    return "Compte gratuit · sans SIRET";
  }

  formatSiret(siret) {
    const s = String(siret).replace(/\s+/g, "");
    if (s.length !== 14) return s;
    return `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6, 9)} ${s.slice(9)}`;
  }

  // ---------------------------------------------------------------------------
  // EPIC-042 / 42.4 — Badge nav « Transactions à traiter »
  // ---------------------------------------------------------------------------

  async hydratePaymentsPending() {
    if (!this.hasPaymentsPendingCountTarget) return;
    try {
      const items = await api.payments.pending();
      const count = Array.isArray(items) ? items.length : 0;
      this._pendingCount = count;
      this._renderPendingBadge();
    } catch (error) {
      // 401 handled globally. Tout autre cas : on laisse le badge masqué.
      // Trace en console pour debug, pas de banner (l'info est secondaire).
      if (!(error instanceof APIError) || error.status !== 401) {
        console.warn("[app-header] hydratePaymentsPending failed", error);
      }
    }
  }

  /**
   * Décrémente le compteur affiché sur le badge nav. Appelée à la fois
   * directement (jamais aujourd'hui — les autres controllers passent par
   * l'event) et via `CustomEvent('nm:pending-decremented')` dispatché sur
   * `window`. Tolérant : no-op si le compteur n'a pas encore été hydraté.
   */
  decrementPending() {
    if (typeof this._pendingCount !== "number" || this._pendingCount <= 0) {
      return;
    }
    this._pendingCount -= 1;
    this._renderPendingBadge();
  }

  _renderPendingBadge() {
    if (!this.hasPaymentsPendingCountTarget) return;
    const count = this._pendingCount;
    const badge = this.paymentsPendingCountTarget;
    if (!count || count <= 0) {
      badge.hidden = true;
      badge.textContent = "";
      return;
    }
    badge.hidden = false;
    const display = count > 99 ? "99+" : String(count);
    badge.textContent = display;
    badge.setAttribute("aria-label", `${count > 99 ? "Plus de 99" : count} transaction${count > 1 ? "s" : ""} à traiter`);
  }
}
